export interface InterviewSummaryQuestion {
  questionCode: string;
  questionLabel?: string;
  questionOrder?: number;
}

export interface InterviewSummaryQuestionGroups<T extends InterviewSummaryQuestion> {
  priority: T[];
  remaining: T[];
}

const priorityOf = (question: InterviewSummaryQuestion): number => {
  const code = question.questionCode.toLowerCase();
  const label = (question.questionLabel || "").toLowerCase();

  if (
    code.includes("nps_dealer") ||
    (label.includes("recommendation") && /dealer|retailer|dealership|workshop/.test(label))
  ) {
    return 0;
  }

  if (label.includes("overall satisfaction")) {
    return 1;
  }

  if (/follow[- ]?up\s*(y\/n|yes\/?no)|\bfu\s*(y\/n|yes\/?no)/.test(label)) {
    return 2;
  }

  if (/overall.*verbatim|overall likes.*verbatim/.test(label)) {
    return 3;
  }

  return Number.MAX_SAFE_INTEGER;
};

export const splitInterviewSummaryQuestions = <T extends InterviewSummaryQuestion>(
  questions: T[]
): InterviewSummaryQuestionGroups<T> => {
  const ordered = questions
    .map((question, index) => ({ question, index, priority: priorityOf(question) }))
    .sort((left, right) => {
      if (left.priority !== right.priority) {
        return left.priority - right.priority;
      }

      const leftOrder = left.question.questionOrder ?? Number.MAX_SAFE_INTEGER;
      const rightOrder = right.question.questionOrder ?? Number.MAX_SAFE_INTEGER;
      return leftOrder - rightOrder || left.index - right.index;
    });

  return {
    priority: ordered.filter(({ priority }) => priority !== Number.MAX_SAFE_INTEGER).map(({ question }) => question),
    remaining: ordered.filter(({ priority }) => priority === Number.MAX_SAFE_INTEGER).map(({ question }) => question),
  };
};
